import { useGetDataScienceMmmOutputResultsQuery } from 'app/apis/queryApi';
import { selectSiteId } from 'features/topbar/selectedSiteSlice';
import { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  selectSelectedModelRunDetails,
  selectSelectedBudgetScenarios,
  selectSelectedSegmentKey,
  selectSelectedTimeType
} from './mmm-controls/mmm-control.slice';
import {
  ColumnResizeMode,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  SortingState,
  useReactTable
} from '@tanstack/react-table';
import { getMmmOutputColumnDefs } from './mmm-output-table-column-defs';
import Tile from 'features/tiles/tile';
import { CustomTable } from 'common/components/table/customTable';
import Loading from 'common/components/loading/loading';
import { CombinedMmmModelOutput } from './models/mmm-model-output';
import MmmDiminishingReturnsValueEditor from './mmm-diminishing-returns-value-editor';
import { createTotalRow, sortRows } from './factories/mmm-output-table-helpers';
import {
  selectIsEditingCurrentSelectedBudgetScenario,
  selectUpdatedBudgetScenarios
} from './edit-budget-scenario.slice';
import { RootState } from 'app/store';

const MmmOutputTable: FC = () => {
  const siteId = useSelector(selectSiteId);
  const segmentKey = useSelector(selectSelectedSegmentKey);
  const budgetScenarios = useSelector(selectSelectedBudgetScenarios);
  const timeRangeType = useSelector(selectSelectedTimeType);
  const modelRunDetails = useSelector(selectSelectedModelRunDetails);

  const modelRunId = modelRunDetails?.modelRunName ?? '';
  const modelRunDate = modelRunDetails?.targetDate ?? '';

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnResizeMode] = useState<ColumnResizeMode>('onChange');

  const primaryBudgetScenario = budgetScenarios?.primarySelectedBudgetScenario;
  const comparedBudgetScenario =
    budgetScenarios?.comparedSelectedBudgetScenario;

  const updatedCompareDataFromBudgetScenario = useSelector((state: RootState) =>
    selectUpdatedBudgetScenarios(state, comparedBudgetScenario)
  );

  const updatedPrimaryDataFromBudgetScenario = useSelector((state: RootState) =>
    selectUpdatedBudgetScenarios(state, primaryBudgetScenario)
  );

  const isCurrentlyEditingChosenComparedModelType = useSelector(
    (state: RootState) =>
      selectIsEditingCurrentSelectedBudgetScenario(
        state,
        comparedBudgetScenario
      )
  );

  const isCurrentlyEditingChosenPrimaryModelType = useSelector(
    (state: RootState) =>
      selectIsEditingCurrentSelectedBudgetScenario(state, primaryBudgetScenario)
  );

  const shouldFetchPrimary =
    !!siteId &&
    !!modelRunId &&
    !!modelRunDate &&
    !!segmentKey &&
    !!primaryBudgetScenario &&
    !!primaryBudgetScenario.scenarioName &&
    !!timeRangeType;

  const shouldFetchCompared =
    !!siteId &&
    !!modelRunId &&
    !!modelRunDate &&
    !!segmentKey &&
    !!comparedBudgetScenario &&
    !!comparedBudgetScenario.scenarioName &&
    !!timeRangeType;

  const {
    data: primaryData,
    isFetching: isFetchingPrimary,
    isUninitialized: isUninitializedPrimary
  } = useGetDataScienceMmmOutputResultsQuery(
    {
      siteId,
      modelRunId,
      modelRunDate,
      segment: segmentKey ?? '',
      modelType: isCurrentlyEditingChosenPrimaryModelType
        ? updatedPrimaryDataFromBudgetScenario?.originalScenarioName ??
          primaryBudgetScenario?.scenarioName ??
          ''
        : primaryBudgetScenario?.scenarioName ?? '',
      timeRangeType: timeRangeType ?? ''
    },
    { skip: !shouldFetchPrimary }
  );

  const {
    data: comparedData,
    isFetching: isFetchingCompared,
    isUninitialized: isUninitializedCompared
  } = useGetDataScienceMmmOutputResultsQuery(
    {
      siteId,
      modelRunId,
      modelRunDate,
      segment: segmentKey ?? '',
      modelType: isCurrentlyEditingChosenComparedModelType
        ? updatedCompareDataFromBudgetScenario?.originalScenarioName ??
          comparedBudgetScenario?.scenarioName ??
          ''
        : comparedBudgetScenario?.scenarioName ?? '',
      timeRangeType: timeRangeType ?? ''
    },
    { skip: !shouldFetchCompared }
  );

  const combinedData = useMemo<CombinedMmmModelOutput[]>(() => {
    if (isFetchingPrimary || isFetchingCompared) {
      return [];
    }

    if (isUninitializedPrimary && isUninitializedCompared) {
      return [];
    }
    if (!primaryData && !comparedData) {
      return [];
    }

    const pk = primaryData;
    const ck = comparedData ?? [];
    const both = pk?.length && ck?.length;

    if (pk?.length === 0 && ck?.length === 0) {
      return [];
    }

    const rows: CombinedMmmModelOutput[] = [];

    if (!both) {
      const [only, isPrimaryOnly] = pk?.length ? [pk, true] : [ck, false];
      for (const r of only) {
        rows.push({
          segmentValue: r.segmentValue,
          spendPrimary: isPrimaryOnly ? r.spend : null,
          spendCompared: !isPrimaryOnly ? r.spend : null,
          revenuePrimary: isPrimaryOnly ? r.revenue : null,
          revenueCompared: !isPrimaryOnly ? r.revenue : null,
          roasPrimary: isPrimaryOnly ? r.roas : null,
          roasCompared: !isPrimaryOnly ? r.roas : null,
          mROASPrimary: isPrimaryOnly ? r.mROAS : null,
          mROASCompared: !isPrimaryOnly ? r.mROAS : null
        });
      }

      const sortedRows =
        sorting.length > 0
          ? sortRows([...(rows ?? [])], sorting[0].id, sorting[0].desc)
          : sortRows([...(rows ?? [])], 'channel', false);

      const allRows = [...sortedRows, createTotalRow(sortedRows)];
      return allRows;
    }

    const pMap = new Map(pk.map((r) => [r.segmentValue, r]));
    const cMap = new Map(ck.map((r) => [r.segmentValue, r]));

    for (const seg of new Set([...pMap.keys(), ...cMap.keys()])) {
      const p = pMap.get(seg);
      const c = cMap.get(seg);
      rows.push({
        segmentValue: seg,
        spendPrimary: p?.spend ?? null,
        revenuePrimary: p?.revenue ?? null,
        roasPrimary: p?.roas ?? null,
        mROASPrimary: p?.mROAS ?? null,
        spendCompared: c?.spend ?? null,
        revenueCompared: c?.revenue ?? null,
        roasCompared: c?.roas ?? null,
        mROASCompared: c?.mROAS ?? null
      });
    }

    const sortedRows =
      sorting.length > 0
        ? sortRows([...(rows ?? [])], sorting[0].id, sorting[0].desc)
        : sortRows([...(rows ?? [])], 'channel', false);

    const allRows = [...sortedRows, createTotalRow(sortedRows)];
    return allRows;
  }, [
    isFetchingPrimary,
    isFetchingCompared,
    isUninitializedPrimary,
    isUninitializedCompared,
    primaryData,
    comparedData,
    sorting
  ]);

  const dataWithUpdatesFromBudgetScenarios = useMemo(() => {
    if (!combinedData || combinedData.length === 0) {
      return [];
    }

    const updatedRows = combinedData.map((row) => {
      if (
        !updatedCompareDataFromBudgetScenario &&
        !updatedPrimaryDataFromBudgetScenario
      ) {
        return row;
      }

      const editedCompareData =
        updatedCompareDataFromBudgetScenario?.updatedValues.find(
          (update) => update.segmentValue === row.segmentValue
        );

      const editedPrimaryData =
        updatedPrimaryDataFromBudgetScenario?.updatedValues.find(
          (update) => update.segmentValue === row.segmentValue
        );

      if (!editedCompareData && !editedPrimaryData) {
        return row;
      } else {
        const updatedRow = {
          ...row,
          spendCompared: editedCompareData?.newSpendValue ?? row.spendCompared,
          revenueCompared:
            editedCompareData?.newRevenueValue ?? row.revenueCompared,
          roasCompared: editedCompareData?.newRoasValue ?? row.roasCompared,
          mROASCompared: editedCompareData?.newMRoasValue ?? row.mROASCompared,
          spendPrimary: editedPrimaryData?.newSpendValue ?? row.spendPrimary,
          revenuePrimary:
            editedPrimaryData?.newRevenueValue ?? row.revenuePrimary,
          roasPrimary: editedPrimaryData?.newRoasValue ?? row.roasPrimary,
          mROASPrimary: editedPrimaryData?.newMRoasValue ?? row.mROASPrimary
        } as CombinedMmmModelOutput;
        return updatedRow;
      }
    });

    return updatedRows;
  }, [
    combinedData,
    updatedCompareDataFromBudgetScenario,
    updatedPrimaryDataFromBudgetScenario
  ]);

  const isCompareMode = shouldFetchPrimary && shouldFetchCompared;

  const columnDefs = useMemo(() => {
    if (!primaryBudgetScenario && !comparedBudgetScenario) {
      return [];
    }

    return getMmmOutputColumnDefs(
      primaryBudgetScenario?.scenarioName,
      comparedBudgetScenario?.scenarioName,
      isCompareMode
    );
  }, [primaryBudgetScenario, comparedBudgetScenario, isCompareMode]);

  const [expanded, setExpanded] = useState<ExpandedState>({});

  const mmmOutputTable = useReactTable({
    data: dataWithUpdatesFromBudgetScenarios,
    columns: columnDefs,
    manualSorting: true,
    state: { sorting, expanded },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: setExpanded,
    getRowCanExpand: (row) => {
      return (
        row.original.spendCompared !== null &&
        [
          'Amazon',
          'Facebook Prospecting',
          'Google Performance Max',
          'Bing NonBrand',
          'Google NonBrand'
        ].includes(row.original.segmentValue)
      );
    },
    enableColumnResizing: true,
    enableFilters: false,
    columnResizeMode
  });

  const renderExpandedRow = (rowId: string, row: CombinedMmmModelOutput) => {
    return (
      <MmmDiminishingReturnsValueEditor
        currentSpend={row.spendCompared ?? 0}
        segmentValue={row.segmentValue}
        segmentKey={segmentKey ?? ''}
        modelRunDate={modelRunDate}
        modelRunId={modelRunId}
        siteId={siteId}
        timeRangeType={timeRangeType ?? ''}
        scenarioName={comparedBudgetScenario?.scenarioName ?? ''}
        budgetScenarioId={comparedBudgetScenario?.budgetId ?? ''}
        onEditConfirmed={() => {
          setExpanded((prev: ExpandedState) => {
            if (prev === true) {
              return false as ExpandedState;
            }
            return {
              ...prev,
              [rowId]: !prev[rowId]
            } as ExpandedState;
          });
        }}
      />
    );
  };

  return (
    <div>
      <h2>MMM Output Table</h2>
      {combinedData.length > 0 ? (
        <div className="col-span-12 md:col-span-6 flex flex-col">
          <Tile className="bg-white border rounded border-neutral-200 p-2.5 shadow-sm grow">
            <div className="p-0">
              {(!isFetchingPrimary || !isFetchingCompared) &&
              (!isUninitializedPrimary || !isUninitializedCompared) ? (
                <CustomTable
                  key={`mmm-output-table-${
                    primaryBudgetScenario?.scenarioName ?? ''
                  }-${comparedBudgetScenario?.scenarioName ?? ''}`}
                  componentId="mmm-output-table-component-id"
                  table={mmmOutputTable}
                  fontsizeClass="text-sm"
                  cellClasses="p-3 truncate"
                  renderCustomExpandedRow={renderExpandedRow}
                />
              ) : (
                <Loading />
              )}
            </div>
          </Tile>
        </div>
      ) : (isFetchingPrimary || isFetchingCompared) &&
        (isUninitializedPrimary || isUninitializedCompared) ? (
        <Loading />
      ) : (
        <p>No data available for selected model type(s).</p>
      )}
    </div>
  );
};

export default MmmOutputTable;
